import { useLingui } from "@lingui/react/macro";
import type { ComputerFileEntry } from "@rakazo/contracts";
import {
  computerFileName,
  computerParentPath,
  computerTerminalDocumentUrl,
  textFilePreview,
} from "@rakazo/contracts";
import { formatFileSize, inferAttachmentMimeType } from "@rakazo/core";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@rakazo/ui-web";
import { ArrowLeft, ChevronDown, FileText, Folder, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { decodeArtifactBase64, downloadArtifactBytes } from "../lib/artifact-open";
import { embeddableScreenUrl } from "../lib/computer-screen";
import { createSpaceRpc, selectedSpaceId } from "../lib/rpc";
import { errorText } from "../lib/user-error";
import { ComputerBrowserBar } from "./ComputerBrowserBar";
import { ComputerScreenFrame } from "./ComputerScreenFrame";
import { ComputerScreenViewport } from "./ComputerScreenViewport";

type ComputerToolsTarget =
  | { botId: string; human?: never }
  | { botId?: never; human: { computerId: string; leaseId?: string; spaceId: string } };

/** Presentation only: the parent retains the existing control lease and heartbeat. */
export function ComputerTools({
  botId,
  human,
  hasControl,
  children,
  recording = false,
}: {
  hasControl: boolean;
  children: ReactNode;
  recording?: boolean;
} & ComputerToolsTarget) {
  const { t } = useLingui();
  if (recording) return <>{children}</>;
  return (
    <Tabs defaultValue="screen" className="h-full min-h-0 gap-0">
      <TabsList variant="line" className="mx-3 my-2 shrink-0" aria-label={t`Computer tools`}>
        <TabsTrigger value="screen">{t`Screen`}</TabsTrigger>
        <TabsTrigger value="terminal">{t`Terminal`}</TabsTrigger>
        <TabsTrigger value="files">{t`Files`}</TabsTrigger>
      </TabsList>
      <TabsContent value="screen" className="flex min-h-0 flex-col">
        {botId && <ComputerBrowserBar key={botId} botId={botId} hasControl={hasControl} />}
        <ComputerScreenViewport>{children}</ComputerScreenViewport>
      </TabsContent>
      <TabsContent value="terminal" className="min-h-0">
        <ComputerTerminal
          key={human?.computerId ?? botId}
          {...(human ? { human } : { botId })}
          hasControl={hasControl}
        />
      </TabsContent>
      <TabsContent value="files" className="min-h-0 overflow-hidden">
        <ComputerFiles key={human?.computerId ?? botId} {...(human ? { human } : { botId })} />
      </TabsContent>
    </Tabs>
  );
}

function ComputerTerminal({
  botId,
  human,
  hasControl,
}: ComputerToolsTarget & { hasControl: boolean }) {
  const { t } = useLingui();
  const [client] = useState(() => createSpaceRpc(human?.spaceId ?? selectedSpaceId()));
  const [result, setResult] = useState<{ url: string | null; error?: string } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const frameRef = useCallback(() => {}, []);
  useEffect(() => {
    setResult(null);
    if (!hasControl) return;
    let live = true;
    const pending = human
      ? client.humanComputer.terminalUrl({ computerId: human.computerId, leaseId: human.leaseId! })
      : client.computer.terminalUrl({ botId });
    void pending.then(
      ({ url }) => {
        if (live) setResult({ url: computerTerminalDocumentUrl(embeddableScreenUrl(url)) });
      },
      (error: unknown) => {
        if (live) setResult({ url: null, error: errorText(error) });
      },
    );
    return () => {
      live = false;
    };
  }, [client, botId, human?.computerId, human?.leaseId, hasControl, refresh]);
  if (hasControl && result?.url)
    return (
      <ComputerScreenFrame
        url={result.url}
        title={t`Computer terminal`}
        frameRef={frameRef}
        allow="clipboard-read; clipboard-write"
      />
    );
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
      {!hasControl ? (
        t`Take control to use the terminal.`
      ) : !result ? (
        <Spinner />
      ) : (
        result.error || t`Terminal is unavailable on this computer.`
      )}
      {hasControl && result && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRefresh((value) => value + 1)}
        >{t`Retry`}</Button>
      )}
    </div>
  );
}

function ComputerFiles({ botId, human }: ComputerToolsTarget) {
  const { t } = useLingui();
  const [client] = useState(() => createSpaceRpc(human?.spaceId ?? selectedSpaceId()));
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState<ComputerFileEntry[] | null>(null);
  const [selected, setSelected] = useState<ComputerFileEntry | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [nonce, setNonce] = useState(() => crypto.randomUUID());
  useEffect(() => {
    let live = true;
    setEntries(null);
    setError("");
    setSelected(null);
    const pending = human
      ? client.humanComputer.files({ computerId: human.computerId, path })
      : client.computer.files({ botId, path });
    void pending.then(
      (items) => {
        if (live)
          setEntries(
            [...items].sort((a, b) =>
              a.kind === b.kind ? a.path.localeCompare(b.path) : a.kind === "dir" ? -1 : 1,
            ),
          );
      },
      (e: unknown) => {
        if (live) setError(errorText(e));
      },
    );
    return () => {
      live = false;
    };
  }, [client, botId, human?.computerId, path, refresh]);
  useEffect(() => {
    let live = true;
    setPreview(null);
    setSaved(null);
    setNonce(crypto.randomUUID());
    if (
      !selected ||
      !textFilePreview(selected.path, inferAttachmentMimeType(selected.path) ?? "", selected.size)
    )
      return;
    setError("");
    const pending = human
      ? client.humanComputer
          .readFile({ computerId: human.computerId, path: selected.path })
          .then((file) => ({
            content: new TextDecoder().decode(decodeArtifactBase64(file.contentBase64)),
          }))
      : client.computer.readFile({ botId, path: selected.path });
    void pending.then(
      (file) => {
        if (live) setPreview(file.content);
      },
      (e: unknown) => {
        if (live) setError(errorText(e));
      },
    );
    return () => {
      live = false;
    };
  }, [client, botId, human?.computerId, selected]);
  async function save() {
    if (!selected || saving || saved) return;
    setSaving(true);
    setError("");
    try {
      const name = computerFileName(selected.path);
      const mimeType = inferAttachmentMimeType(name) ?? "application/octet-stream";
      if (human) {
        const file = await client.humanComputer.readFile({
          computerId: human.computerId,
          path: selected.path,
        });
        downloadArtifactBytes(name, mimeType, decodeArtifactBase64(file.contentBase64));
        return;
      }
      const file = await client.computer.downloadFile({ botId, path: selected.path });
      const artifact = await client.artifacts.create({
        botId,
        name,
        mimeType,
        contentBase64: file.contentBase64,
        clientNonce: nonce,
      });
      setSaved(artifact.id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }
  const rootLabel = human ? t`Computer home` : t`Bot files`;
  const canPreview =
    selected &&
    textFilePreview(selected.path, inferAttachmentMimeType(selected.path) ?? "", selected.size);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-w-0 items-center gap-2 px-3 py-2">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t`Back`}
          disabled={saving || (!selected && (path === "" || path === "/"))}
          onClick={() => (selected ? setSelected(null) : setPath(computerParentPath(path)))}
        >
          <ArrowLeft size={16} />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={saving}
            aria-label={t`File location`}
            title={selected?.path ?? (path || rootLabel)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted/40"
          >
            <span className="min-w-0 flex-1 truncate font-mono">
              {selected?.path ?? (path === "/" ? t`Computer home` : path || rootLabel)}
            </span>
            <ChevronDown size={13} className="shrink-0 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onClick={() => {
                setSelected(null);
                setPath("");
              }}
            >
              {rootLabel}
            </DropdownMenuItem>
            {!human && (
              <DropdownMenuItem
                onClick={() => {
                  setSelected(null);
                  setPath("/");
                }}
              >
                {t`Computer home`}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {selected ? (
          saved ? (
            <Link
              className="shrink-0 text-xs underline"
              to={`/app/artifacts/${encodeURIComponent(saved)}?version=${encodeURIComponent(saved)}`}
            >{t`Open in Files`}</Link>
          ) : (
            <Button size="sm" variant="outline" disabled={saving} onClick={() => void save()}>
              {saving ? <Spinner /> : human ? t`Download` : t`Save to Files`}
            </Button>
          )
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Refresh`}
            onClick={() => setRefresh((value) => value + 1)}
          >
            <RefreshCw size={15} />
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="px-5 py-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-auto px-3 pb-4">
        {selected ? (
          preview !== null ? (
            <pre className="whitespace-pre-wrap break-words px-2 font-mono text-xs leading-relaxed">
              {preview}
            </pre>
          ) : canPreview && !error ? (
            <Spinner />
          ) : (
            !error && (
              <p className="p-3 text-xs text-muted-foreground">{formatFileSize(selected.size)}</p>
            )
          )
        ) : entries === null ? (
          !error && <Spinner />
        ) : entries.length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">{t`This folder is empty.`}</p>
        ) : (
          entries.map((entry) => (
            <button
              key={entry.path}
              type="button"
              className="flex w-full min-w-0 items-center gap-3 rounded-lg px-2 py-2.5 text-left text-xs hover:bg-muted/40"
              onClick={() => (entry.kind === "dir" ? setPath(entry.path) : setSelected(entry))}
            >
              {entry.kind === "dir" ? (
                <Folder size={17} className="shrink-0 text-muted-foreground" />
              ) : (
                <FileText size={17} className="shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1 truncate">{computerFileName(entry.path)}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {entry.kind === "file" ? formatFileSize(entry.size) : ""}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
